import React, { memo, useContext } from 'react';
import { Box, alpha, useTheme } from '@mui/material';
import MessageText from './message-text';
import MessageFile from './message-file';
import MessageStatus from './message-status';
import MessageReplyPreview from './message-reply-preview';
import MessageTime from './message-time';
import { Message } from '../../../type/chat.types';
import { MessageContext } from '../chat-messages';

type MessageContainerProps = {
    message: Message;
    isMe: boolean;
    isUserSide?: boolean;
    index: number;
};

const MessageContainer = ({
    message,
    isMe,
    isUserSide = isMe,
    index,
}: MessageContainerProps) => {
    const theme = useTheme();
    const messageHook = useContext(MessageContext);
    const { isSending, isMessageError, isRestoreDelete } = messageHook!;

    const hasFile = message?.files?.length > 0;
    const hasText = !!message?.text;
    const isActive = message?.status === 'active';

    return (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                p: 1.5,
                fontSize: 14,
                borderRadius: isUserSide ? '6px 16px 16px 16px' : '16px 6px 16px 16px',
                bgcolor: !isActive
                    ? alpha(theme.palette.error.main, 0.12)
                    : isUserSide
                      ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.09)
                      : theme.palette.background.paper,
                border: '1px solid',
                borderColor: !isActive
                    ? alpha(theme.palette.error.main, 0.2)
                    : isUserSide
                      ? alpha(theme.palette.primary.main, 0.16)
                      : theme.palette.divider,
                boxShadow:
                    theme.palette.mode === 'dark'
                        ? 'none'
                        : '0 4px 14px rgba(20, 25, 40, 0.04)',
                opacity:
                    !message.isNotSent &&
                    (isSending || isRestoreDelete || isMessageError)
                        ? 0.7
                        : 1,
                '& > :last-child': {
                    alignSelf: isUserSide ? 'flex-end' : 'flex-start',
                },
            }}>
            <MessageReplyPreview message={message} />
            {hasText && (
                <MessageText message={message} isMe={isUserSide} hasFile={hasFile} />
            )}
            {hasFile && <MessageFile message={message} isMe={isUserSide} />}
            <MessageStatus
                message={message}
                isMe={isMe}
                hasFile={hasFile}
                index={index}
            />
            <MessageTime message={message} isMe={isUserSide} />
        </Box>
    );
};

export default memo(MessageContainer);
